import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';

import { RouterOutlet, RouterLink } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { AuthenticationOidcService } from '../../services/authentication-oidc-service';
import { MenuService } from '../../services/menu-service';
import { MenuItem } from '../../interfaces/menu-item';
import { MenuResponse } from '../../interfaces/menu-response';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MenuParentsPipe, MenuChildrenPipe } from '../../pipes/menu-hierarchy.pipes';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatProgressBarModule,
    MenuParentsPipe,
    MenuChildrenPipe,
  ],
  templateUrl: './layout.html',
  styleUrls: ['./layout.scss'],
})
export class Layout implements OnInit {
  isAuthenticated: boolean = false;
  userName: string = '';
  menu: MenuItem[] = [];
  isLoadingMenu: boolean = false;
  expandedCategories: Set<number> = new Set<number>();
  selectedMenuItemUrl: string | null = null;
  shouldHideNavigation: boolean = false;

  // Routes that should not display navigation (sidebar and toolbar)
  private readonly ROUTES_WITHOUT_NAVIGATION = ['/unauthorized', '/login-callback'];

  constructor(
    private readonly authenticationOidcService: AuthenticationOidcService,
    private readonly menuService: MenuService,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    // Check current route
    this.checkIfUnauthorizedPage(this.router.url);

    // Subscribe to route changes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.checkIfUnauthorizedPage(event.urlAfterRedirects);
      });

    this.isAuthenticated = this.authenticationOidcService.isLoggedIn;
    if (this.isAuthenticated) {
      this.authenticationOidcService.getUserInfo().subscribe((userInfo) => {
        this.userName = userInfo.name;
        if (userInfo.email) {
          this.isLoadingMenu = true;
          this.menuService.getMenu(userInfo.email).subscribe((result: MenuResponse) => {
            this.isLoadingMenu = false;
            if (result.error) {
              const snackBarDuration = 10000;
              this.snackBar.open(result.error, 'Close', { duration: snackBarDuration });
              this.menu = [];
            } else {
              this.menu = result.items;
            }
          });
        }
      });
    } else {
      this.menu = [];
      this.userName = '';
    }
  }

  // Toggles the expansion state of a category
  toggleCategory(categoryId: number): void {
    if (this.expandedCategories.has(categoryId)) {
      this.expandedCategories.delete(categoryId);
    } else {
      this.expandedCategories.add(categoryId);
    }
  }

  // Checks if a category is currently expanded
  isCategoryExpanded(categoryId: number): boolean {
    return this.expandedCategories.has(categoryId);
  }

  // Navigates to the report view for the selected menu item
  selectReport(item: MenuItem): void {
    if (item.url && item.powerBIReportId && item.powerBIWorkspaceId) {
      this.selectedMenuItemUrl = item.url;
      this.router.navigate(['/reports', item.url], {
        queryParams: {
          g: item.powerBIWorkspaceId,
          r: item.powerBIReportId,
        },
      });
    }
  }

  /**
   * Checks if the current route should hide navigation elements (sidebar menu and toolbar).
   * Returns true for routes like unauthorized and login-callback pages.
   * @param url - The current route URL
   */
  private checkIfUnauthorizedPage(url: string): void {
    // Extract the path without query parameters
    const path = url.split('?')[0];
    
    // Check if current path matches any route that should hide navigation
    this.shouldHideNavigation = this.ROUTES_WITHOUT_NAVIGATION.some(route => 
      path === route || path.startsWith(route + '/')
    );
  }
}
